import { type ClusterGroup } from "~/HousingSelectionAssistant/types/types";
import { type FacetData, type FacetValueData } from "~/utils/models";
import { HOUSING_SA_PURE_FACET_FILTERS, HOUSING_SA_TRUE_CLUSTERS } from "~/HousingSelectionAssistant/constants";
import { toMapFn } from "~/utils/reducers";
import { type FilterDefinitionV2, type ItemDefinitionV2 } from "~/HousingSelectionAssistant/types/FilterDefinitionV2";

function getSuffixNameFromCluster(clusterItemCode: string): string {
  if (clusterItemCode.includes("__")) {
    return clusterItemCode.split("__")[0];
  } else {
    return clusterItemCode;
  }
}

export function createFilterDefinitions(
  clusterGroups: ClusterGroup[],
  facetsByCode: Record<string, FacetData>,
): Record<string, FilterDefinitionV2> {
  const filters: FilterDefinitionV2[] = clusterGroups.map((cluster) => {
    const clusterCode = getSuffixNameFromCluster(cluster.clusterItems[0].code);

    let items: ItemDefinitionV2[];
    if (HOUSING_SA_TRUE_CLUSTERS.includes(cluster.code)) {
      items = cluster.clusterItems.map((item) => {
        const facet = facetsByCode[item.code];

        return {
          label: item.text,
          code: item.code,
          facetCode: facet?.code, // there is no facet code for True Clusters
          facetValueCode: "true",
          imageUrl: item.imageUrl,
          activeImageUrl: item.activeImageUrl,
        };
      });
    } else if (HOUSING_SA_PURE_FACET_FILTERS.includes(cluster.code)) {
      const facet = facetsByCode[clusterCode];
      items = facet?.values.map((item) => {
        return {
          label: item.name,
          code: item.code,
          facetCode: facet.code,
          facetValueCode: item.code,
        };
      });
    } else {
      items = cluster.clusterItems
        .map((item) => {
          const facet = facetsByCode[getSuffixNameFromCluster(item.code)];
          const facetValue = (facet?.values.find((itemValue) =>
            itemValue.code.includes("_")
              ? itemValue.code.substring(0, itemValue.code.lastIndexOf("_")) == item.code.replace("__", "_")
              : itemValue.code == item.code.replace("__", "_"),
          ) ?? {}) as FacetValueData;
          // PIM does not provide suffix/attribute translations, therefore we'll keep them in impex via cluster items
          facetValue["name"] = item.text;
          const facetCode = facet?.code ?? "";
          return [facetValue, facetCode, item] as const;
        })
        .map(([val, facetCode, item]) => ({
          label: val.name,
          code: val.code,
          facetCode: facetCode,
          facetValueCode: val.code,
          imageUrl: item.imageUrl,
        }));
    }

    return {
      label: cluster.name,
      description: cluster.description,
      code: cluster.code,
      items,
    };
  });

  return toMapFn(filters, (filter) => filter.code);
}
